import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  TablePagination,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from '../components/AppLayout';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import type { LeaveAuditEvent } from '../types';
import type { PagedResult } from '../types/pagedResult';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import ClearIcon from '@mui/icons-material/Clear';
import CalculateIcon from '@mui/icons-material/Calculate';
import { formatDate } from '../utils/dateFormat';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad, pagination, permissionCopy, displayTimeZoneCopy } from '../utils/userFacingCopy';
import {
  RecalculateWorkingDaysResultDialog,
  type RecalculateWorkingDaysResult,
} from '../components/RecalculateWorkingDaysResultDialog';

const EVENT_TYPE_LABELS: Record<string, string> = {
  LeaveRequestApproved: 'Leave Request Approved',
  LeaveRequestSubmitted: 'Leave Request Submitted',
  LeaveRequestWasApproved: 'Leave Was Approved',
  LeaveRequestWithdrawn: 'Leave Request Withdrawn',
  LeaveRequestCreatedOnBehalf: 'Leave Request Created On Behalf',
  LeaveRequestSeniorApproved: 'Leave Request Senior Approved',
  LeaveRequestWasSeniorApproved: 'Leave Was Senior Approved',
  LeaveRequestUpdated: 'Leave Request Updated',
  LeaveRequestWithdrawalRequested: 'Leave Withdrawal Requested',
  LeaveRequestHrApprovalRequested: 'Leave HR Approval Requested',
  LeaveRequestHrApproved: 'Leave HR Approved',
  LeaveRequestHrRejected: 'Leave HR Rejected',
  WorkingDaysRecalculatedByAdministrator: 'Working Days Recalculated (Admin)',
  ImportedLeaveBalanceUpdated: 'Imported Leave Balance Updated',
  ImportedLeaveBalanceDeleted: 'Imported Leave Balance Deleted',
  EvidenceUploaded: 'Supporting Document Uploaded',
  EvidenceDeleted: 'Supporting Document Deleted',
};

/** API may return eventType as number (enum). */
const EVENT_TYPE_BY_NUMBER: Record<number, string> = {
  1: 'LeaveRequestApproved',
  2: 'LeaveRequestSubmitted',
  3: 'LeaveRequestWasApproved',
  4: 'LeaveRequestWithdrawn',
  5: 'LeaveRequestCreatedOnBehalf',
  6: 'LeaveRequestSeniorApproved',
  7: 'LeaveRequestWasSeniorApproved',
  8: 'LeaveRequestUpdated',
  9: 'LeaveRequestWithdrawalRequested',
  10: 'LeaveRequestHrApprovalRequested',
  11: 'LeaveRequestHrApproved',
  12: 'LeaveRequestHrRejected',
  13: 'WorkingDaysRecalculatedByAdministrator',
  14: 'ImportedLeaveBalanceUpdated',
  15: 'ImportedLeaveBalanceDeleted',
  16: 'EvidenceUploaded',
  17: 'EvidenceDeleted',
};

const EVENT_TYPES = [
  'all',
  'LeaveRequestApproved',
  'LeaveRequestSubmitted',
  'LeaveRequestWasApproved',
  'LeaveRequestWithdrawn',
  'LeaveRequestCreatedOnBehalf',
  'LeaveRequestSeniorApproved',
  'LeaveRequestWasSeniorApproved',
  'LeaveRequestUpdated',
  'LeaveRequestWithdrawalRequested',
  'LeaveRequestHrApprovalRequested',
  'LeaveRequestHrApproved',
  'LeaveRequestHrRejected',
  'WorkingDaysRecalculatedByAdministrator',
  'ImportedLeaveBalanceUpdated',
  'ImportedLeaveBalanceDeleted',
  'EvidenceUploaded',
  'EvidenceDeleted',
];

export default function LeaveAuditLogPage() {
  const { user, displayTimeZoneId } = useAuth();
  const canAccess = (user?.roles.some((r) => r === 'Administrator' || r === 'SeniorManager')) ?? false;
  const isAdmin = user?.roles.some((r) => r === 'Administrator') ?? false;
  const [auditEvents, setAuditEvents] = useState<LeaveAuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(pagination.auditLogDefaultPageSize);
  const [totalCount, setTotalCount] = useState(0);

  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const [confirmRecalcOpen, setConfirmRecalcOpen] = useState(false);
  const [recalcResultOpen, setRecalcResultOpen] = useState(false);
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [recalcError, setRecalcError] = useState('');
  const [recalcResult, setRecalcResult] = useState<RecalculateWorkingDaysResult | null>(null);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
    }, 400);
    return () => window.clearTimeout(handle);
  }, [searchQuery]);

  const loadAuditEvents = useCallback(async () => {
    if (!canAccess) {
      setError(permissionCopy.administratorOrSeniorManager);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams();
      params.append('page', (page + 1).toString());
      params.append('pageSize', rowsPerPage.toString());
      if (debouncedSearchQuery.trim()) params.append('searchQuery', debouncedSearchQuery.trim());
      if (eventTypeFilter && eventTypeFilter !== 'all') params.append('eventTypeFilter', eventTypeFilter);

      const response = await apiService.get<PagedResult<LeaveAuditEvent>>(
        `${API_ENDPOINTS.LEAVE.AUDIT_LOG}?${params.toString()}`
      );
      setAuditEvents(response.data.items);
      setTotalCount(response.data.totalCount);
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotLoad('audit events')));
    } finally {
      setLoading(false);
    }
  }, [canAccess, page, rowsPerPage, debouncedSearchQuery, eventTypeFilter]);

  useEffect(() => {
    loadAuditEvents();
  }, [loadAuditEvents]);

  const getEventTypeLabel = (eventType: string | number): string => {
    const key = typeof eventType === 'number' ? (EVENT_TYPE_BY_NUMBER[eventType] ?? String(eventType)) : String(eventType);
    return EVENT_TYPE_LABELS[key] ?? key;
  };

  const handleSearchChange = (value: string) => setSearchQuery(value);
  const handleClearSearch = () => setSearchQuery('');
  const handleEventTypeFilterChange = (value: string) => setEventTypeFilter(value);

  const handleRunRecalculate = async () => {
    setConfirmRecalcOpen(false);
    setRecalcLoading(true);
    setRecalcError('');
    try {
      const response = await apiService.post<RecalculateWorkingDaysResult>(
        API_ENDPOINTS.LEAVE.RECALCULATE_WORKING_DAYS,
        {}
      );
      setRecalcResult(response.data);
      setRecalcResultOpen(true);
      await loadAuditEvents();
    } catch (err: unknown) {
            setRecalcError(getApiErrorMessage(err, 'Recalculation failed'));
    } finally {
      setRecalcLoading(false);
    }
  };

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <HistoryIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
          <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
            Leave Audit Log
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          This page shows all leave-related audit events across the system for compliance reporting.{' '}
          {displayTimeZoneCopy.auditLogTimesHint}
        </Alert>

        {isAdmin && (
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              startIcon={recalcLoading ? <CircularProgress size={16} /> : <CalculateIcon />}
              disabled={recalcLoading}
              onClick={() => {
                setRecalcError('');
                setConfirmRecalcOpen(true);
              }}
              sx={{ textTransform: 'none' }}
            >
              Recalculate all leave working days
            </Button>
          </Box>
        )}

        {recalcError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRecalcError('')}>
            {recalcError}
          </Alert>
        )}

        <Dialog open={confirmRecalcOpen} onClose={() => !recalcLoading && setConfirmRecalcOpen(false)}>
          <DialogTitle>Recalculate working days for all leave?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              We&apos;ll recalculate working days on every leave booking using the country and public holidays from Settings.
              Any change is recorded in the audit trail; refresh this page after it runs to see new events.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmRecalcOpen(false)} disabled={recalcLoading}>
              Cancel
            </Button>
            <Button onClick={handleRunRecalculate} color="warning" variant="contained" disabled={recalcLoading}>
              Run
            </Button>
          </DialogActions>
        </Dialog>

        <RecalculateWorkingDaysResultDialog
          open={recalcResultOpen}
          onClose={() => setRecalcResultOpen(false)}
          result={recalcResult}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Search audit events"
                placeholder="Search by employee, actor, or details..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleClearSearch} edge="end" sx={{ mr: -1 }}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl sx={{ minWidth: 220 }}>
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={eventTypeFilter}
                  label="Event Type"
                  onChange={(e) => handleEventTypeFilterChange(e.target.value)}
                >
                  {EVENT_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type === 'all' ? 'All Events' : getEventTypeLabel(type)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {(searchQuery.trim() !== '' || eventTypeFilter !== 'all') && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                {searchQuery.trim() !== '' && (
                  <Chip label={`Search: "${searchQuery}"`} onDelete={handleClearSearch} size="small" color="primary" />
                )}
                {eventTypeFilter !== 'all' && (
                  <Chip
                    label={`Event Type: ${getEventTypeLabel(eventTypeFilter)}`}
                    onDelete={() => handleEventTypeFilterChange('all')}
                    size="small"
                    color="info"
                  />
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table key={displayTimeZoneId}>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Timestamp</strong></TableCell>
                    <TableCell><strong>Event Type</strong></TableCell>
                    <TableCell><strong>Employee</strong></TableCell>
                    <TableCell><strong>Actor</strong></TableCell>
                    <TableCell><strong>Details</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditEvents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 0, border: 'none' }}>
                        <Box sx={{ py: 4 }}>
                          <HistoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            No Audit Events Found
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {searchQuery || eventTypeFilter !== 'all'
                              ? 'Try adjusting your search or filter criteria.'
                              : 'No leave audit events have been recorded yet. Events will appear here as leave requests are submitted, approved, or withdrawn.'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    auditEvents.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell>{formatDate(event.eventAt, { includeTime: true })}</TableCell>
                        <TableCell>{getEventTypeLabel(event.eventType)}</TableCell>
                        <TableCell>{event.userName || '-'}</TableCell>
                        <TableCell>{event.actorName || '-'}</TableCell>
                        <TableCell>{event.details || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[...pagination.auditLogPageSizeOptions]}
            />
          </>
        )}
      </Box>
    </AppLayout>
  );
}
